import { find } from "lodash";
import { AICC_ASSET_TYPE, AiccAssetType } from "./aiccLanguage";

/**
 * Prompt management (AICC2-60 / AICC2-328).
 *
 * 🔴 THE CATALOGUE IS THE BACKEND'S. Which prompts exist, what they are called, which variables each
 * one takes and whether a variable is mandatory all come from the database — so none of that is here.
 * What is here is the page's own structure: the four tabs, and how a source badge is worded and
 * coloured.
 *
 * The tabs are the four Prompt sub-assets of a language, seen from the other side: Language
 * Management lists them as rows with a Manage button, and each of those buttons opens this screen on
 * its own tab.
 */
export const AICC_PROMPT_GROUP = {
  onboarding: "onboarding",
  scenarioConversation: "scenario_conversation",
  lessonReview: "lesson_review",
  supportingFunctions: "supporting_functions",
} as const;

export type AiccPromptGroup = typeof AICC_PROMPT_GROUP[keyof typeof AICC_PROMPT_GROUP];

export const AICC_PROMPT_GROUP_LABEL: Record<AiccPromptGroup, string> = {
  [AICC_PROMPT_GROUP.onboarding]: "Onboarding",
  [AICC_PROMPT_GROUP.scenarioConversation]: "Scenario Conversation",
  [AICC_PROMPT_GROUP.lessonReview]: "Lesson Review",
  [AICC_PROMPT_GROUP.supportingFunctions]: "Supporting Functions",
};

/** Tab order, which is the order the tickets list the four modules in. */
export const AICC_PROMPT_GROUPS_IN_ORDER: AiccPromptGroup[] = [
  AICC_PROMPT_GROUP.onboarding,
  AICC_PROMPT_GROUP.scenarioConversation,
  AICC_PROMPT_GROUP.lessonReview,
  AICC_PROMPT_GROUP.supportingFunctions,
];

/**
 * The tab in the URL rather than in a store, so a tab is shareable and survives a refresh — the same
 * rule the list screens follow for their filters. Hyphens because the rest of the app's paths use
 * them; the underscored values above are the backend's spelling and stay out of the address bar.
 */
export const AICC_PROMPT_GROUP_SLUG: Record<AiccPromptGroup, string> = {
  [AICC_PROMPT_GROUP.onboarding]: "onboarding",
  [AICC_PROMPT_GROUP.scenarioConversation]: "scenario-conversation",
  [AICC_PROMPT_GROUP.lessonReview]: "lesson-review",
  [AICC_PROMPT_GROUP.supportingFunctions]: "supporting-functions",
};

/** The tab a URL is pointing at, or nothing when the slug is not one of the four. */
export function aiccPromptGroupFromSlug(slug?: string): AiccPromptGroup | undefined {
  return find(AICC_PROMPT_GROUPS_IN_ORDER, (group) => AICC_PROMPT_GROUP_SLUG[group] === slug);
}

/**
 * Which tab a sub-asset's Manage button opens.
 *
 * 🔴 The fourth pair does not match by name: the sub-asset is `feedback_assessment_prompt` and the tab
 * is Supporting Functions, which holds Feedback Summary and Foreign Word Identification. Deriving one
 * from the other would work for three of the four and quietly fail on that one.
 */
export const AICC_PROMPT_GROUP_BY_ASSET_TYPE: Partial<Record<AiccAssetType, AiccPromptGroup>> = {
  [AICC_ASSET_TYPE.onboardingPrompt]: AICC_PROMPT_GROUP.onboarding,
  [AICC_ASSET_TYPE.scenarioConversationPrompt]: AICC_PROMPT_GROUP.scenarioConversation,
  [AICC_ASSET_TYPE.lessonReviewPrompt]: AICC_PROMPT_GROUP.lessonReview,
  [AICC_ASSET_TYPE.feedbackAssessmentPrompt]: AICC_PROMPT_GROUP.supportingFunctions,
};

/**
 * Whether a prompt still runs the system default or has been tailored for this language.
 *
 * Derived server-side from the payload: model, temperature or text diverging from the default schema
 * is what makes a prompt language-specific, so nothing here compares configs to decide it.
 */
export const AICC_PROMPT_SOURCE = {
  default: "default",
  languageSpecific: "language_specific",
} as const;

export type AiccPromptSource = typeof AICC_PROMPT_SOURCE[keyof typeof AICC_PROMPT_SOURCE];

export const AICC_PROMPT_SOURCE_LABEL: Record<AiccPromptSource, string> = {
  [AICC_PROMPT_SOURCE.default]: "Default",
  [AICC_PROMPT_SOURCE.languageSpecific]: "Language-Specific",
};

/**
 * Which copy a prompt's tailoring lives in — `source_scope` on a row, and `scope` on a version group.
 *
 * No longer used to label the text on the detail page: that answer now comes from which of the three
 * config keys carries it, so nothing has to be read to find out.
 */
export const AICC_PROMPT_SCOPE = {
  /** The unpublished draft: the only copy that can be written to. */
  working: "working",
  /** A published version. */
  version: "version",
  /** The base set every language starts from. */
  system: "system",
} as const;

export type AiccPromptScope = typeof AICC_PROMPT_SCOPE[keyof typeof AICC_PROMPT_SCOPE];

/** Grey for untouched, blue for tailored — the same grey and blue the language statuses use. */
export const AICC_PROMPT_SOURCE_COLOR: Record<AiccPromptSource, string> = {
  [AICC_PROMPT_SOURCE.default]: "#9c9c9c",
  [AICC_PROMPT_SOURCE.languageSpecific]: "#2f9ce1",
};

/**
 * What a reset restores this prompt's text to (AICC2-526).
 *
 * 🔴 TEXT ONLY, AND ONLY THIS PROMPT. The endpoint used to delete the whole flow record, which took the
 * flow's other prompts and its model configuration with it; it now rewrites the text of the one prompt
 * named in the path. `default` is the base set every language shares, `public` is this language's own
 * published version — the backend rejects anything else with a 422.
 */
export const AICC_PROMPT_RESET_TARGET = {
  default: "default",
  public: "public",
} as const;

export type AiccPromptResetTarget = typeof AICC_PROMPT_RESET_TARGET[keyof typeof AICC_PROMPT_RESET_TARGET];

/**
 * The effort a model that reasons gets when nothing else says otherwise.
 *
 * 🔴 THE VALUE THE BACKEND'S OWN DEFAULTS CARRY, which is why it is the fallback rather than the first
 * entry in a model's list: picking that would silently move a prompt to whatever the catalogue happens to
 * order first. Still guarded by `includes` at the point of use — a model that reasons but does not offer
 * `none` gets its first value, since an effort outside a model's set is a hard 400.
 */
export const AICC_PROMPT_DEFAULT_EFFORT = "none";

/**
 * The temperature slider's range (AICC2-328 AC-2). 0 to 1, confirmed with the reporter — narrower than
 * what the models themselves accept, and deliberately so: this screen offers the range the product uses.
 */
export const AICC_PROMPT_TEMPERATURE = {
  min: 0,
  max: 1,
  step: 0.1,
};
